import { action, observable, reaction, runInAction } from 'mobx';
import { IButtonProps } from 'telia-front-react';
import ErrorUtil from '../error/ErrorUtil';
import API from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';
import LanguageStore from './LanguageStore';
import { RootStore } from './RootStore';

const DEFAULT_TAG = 'default';
const DEFAULT_CATEGORY = 'default';

export interface NewsItem {
  title: string;
  slug: string;
  featured: boolean;
  isoDate?: string;
  displayDate?: string;
  id?: number;
  url?: string;
  date?: string;
  button?: IButtonProps;
  link?: string;
  tags?: string[];
}

export interface NewsCategory {
  itemsMap: Map<number, NewsItemsHolder>;
  total?: number;
}

interface NewsItemsHolder {
  items: NewsItem[];
  status?: FetchStatus;
}

class NewsStore {
  @observable accessor news = new Map<string, NewsCategory>();
  @observable accessor currentPage: number = 1;

  private languageStore: LanguageStore;

  constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;

    reaction(
      () => this.languageStore.currentLanguage,
      () => this.setCurrentPage(1)
    );
  }

  @action.bound
  public setCurrentPage(page: number) {
    this.currentPage = page;
  }

  public getNewsItemsForPage = (perPage: number = 5, tag?: string, category?: string) => {
    return this.getNewsCategoryCurrentPage(tag, category).items.slice(0, perPage);
  };

  public areCurrentPageNewsItemsFetching = (tag?: string, category?: string) => {
    return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.getNewsCategoryCurrentPage(tag, category).status);
  };

  public getNewsItemsTotalCount = (tag?: string, category?: string) => {
    return this.getNewsCategory(tag, category).total;
  };

  public getTotalPages = (perPage: number = 5, tag?: string, category?: string): number => {
    return Math.ceil(this.getNewsItemsTotalCount(tag, category) / perPage) || 0;
  };

  public newsNotFound = (tag?: string, category?: string) => {
    return this.getNewsCategoryCurrentPage(tag, category).status === FetchStatus.NOT_FOUND;
  };

  public loadNews = async (perPage: number = 5, tag?: string, category?: string) => {
    const currentPageNewsAmount = this.getNewsCategoryCurrentPage(tag, category).items.length;
    if (currentPageNewsAmount >= perPage || (this.getTotalPages(perPage, tag, category) === this.currentPage && currentPageNewsAmount !== 0)) {
      return Promise.resolve();
    }
    return this.fetchNews(perPage, tag, category);
  };

  private fetchNews = async (amount: number, tag: string, category: string) => {
    const params = {
      context: 'embed',
      perPage: amount,
      offset: (this.currentPage - 1) * amount,
      tags: tag === 'default' ? [] : [tag],
      categories: category === 'default' ? [] : [category]
    };
    try {
      runInAction(() => {
        this.getNewsCategoryCurrentPage(tag, category).status = FetchStatus.FETCHING;
      });
      const response = await API.get(`/news/${this.languageStore.currentLanguage}`, { params });
      this.addNewsToMap(response.data, tag, category);
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      runInAction(() => {
        this.getNewsCategoryCurrentPage(tag, category).status = FetchStatus.NOT_FOUND;
      });
    }
  };

  private addNewsToMap = (data: { items: NewsItem[]; total: number }, tag: string, category: string) => {
    const newsCategory = this.getNewsCategory(tag, category);
    runInAction(() => {
      newsCategory.itemsMap.set(this.currentPage, { items: data.items, status: FetchStatus.FETCHED });
      newsCategory.total = data.total;
    });
  };

  private getNewsCategory = (tag: string, category: string) => {
    const key = this.buildKey(tag, category);
    if (!this.news.get(key)) {
      runInAction(() => {
        this.news.set(key, { itemsMap: new Map([[this.currentPage, { items: [], status: FetchStatus.UNINITIALIZED }]]), total: 0 });
      });
    }
    if (!this.news.get(key).itemsMap.get(this.currentPage)) {
      this.news.set(key, {
        itemsMap: this.news.get(key).itemsMap.set(this.currentPage, { items: [], status: FetchStatus.UNINITIALIZED }),
        total: this.news.get(key).total
      });
    }
    return this.news.get(key);
  };

  private getNewsCategoryCurrentPage = (tag: string, category: string) => {
    return this.getNewsCategory(tag, category).itemsMap.get(this.currentPage);
  };

  private buildKey(tag: string = DEFAULT_TAG, category: string = DEFAULT_CATEGORY) {
    return `${tag}_${category}_${this.languageStore.currentLanguage}`;
  }
}

export default NewsStore;
